/**
 * Git Path Configuration Component
 * Shows git path status and allows configuration for Basecamp projects
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { AlertCircle, Code2, CheckCircle2, Settings, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { fetchWithAuth } from '@/lib/api';

interface GitPathConfigProps {
  projectId: string | number;
  projectName: string;
  variant?: 'badge' | 'button' | 'inline';
  onUpdate?: () => void;
}

interface GitConfig {
  local_git_path: string | null;
  remote_git_url: string | null;
  default_branch: string;
}

/**
 * Document viewer, the folder toolbar, and the Basecamp todo view each mount a
 * hidden GitPathConfig that listens for the same event. Without a single opener,
 * one click stacks a dialog per mount.
 */
type GitConfigOpener = {
  projectId: string;
  priority: number;
  open: () => void;
};

const gitConfigOpeners: GitConfigOpener[] = [];

function openSingleGitConfig(projectId: string) {
  const matches = gitConfigOpeners.filter((opener) => opener.projectId === projectId);
  if (matches.length === 0) return;
  const best = matches.reduce((chosen, opener) =>
    opener.priority > chosen.priority ? opener : chosen
  );
  best.open();
}

export function GitPathConfig({
  projectId,
  projectName,
  variant = 'badge',
  onUpdate
}: GitPathConfigProps) {
  const [isOpen, setIsOpen] = useState(false);

  const projectKey = String(projectId);
  const openerPriority = onUpdate ? 2 : projectName && projectName !== 'Project' ? 1 : 0;

  useEffect(() => {
    const opener: GitConfigOpener = {
      projectId: projectKey,
      priority: openerPriority,
      open: () => setIsOpen(true),
    };
    gitConfigOpeners.push(opener);
    return () => {
      const index = gitConfigOpeners.indexOf(opener);
      if (index >= 0) gitConfigOpeners.splice(index, 1);
    };
  }, [projectKey, openerPriority]);

  // Listen for configure git event from context menu. The first listener claims
  // the event and opens only the highest-priority mount for that project.
  useEffect(() => {
    const handleConfigureGit = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: string | number }>).detail;
      const eventProjectId = detail?.projectId;
      if (eventProjectId == null || String(eventProjectId) !== projectKey) return;
      const marked = event as CustomEvent & { __gitConfigHandled?: boolean };
      if (marked.__gitConfigHandled) return;
      marked.__gitConfigHandled = true;
      openSingleGitConfig(projectKey);
    };

    window.addEventListener('basecamp-project-configure-git', handleConfigureGit);
    return () => {
      window.removeEventListener('basecamp-project-configure-git', handleConfigureGit);
    };
  }, [projectKey]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [gitConfig, setGitConfig] = useState<GitConfig | null>(null);
  const [localGitPath, setLocalGitPath] = useState('');
  const [remoteGitUrl, setRemoteGitUrl] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('main');

  // Fetch git config on mount (for inline/badge variants) and when dialog opens
  useEffect(() => {
    if (variant === 'inline' || variant === 'badge' || isOpen) {
      fetchGitConfig();
    }
  }, [isOpen, projectId, variant]);

  const fetchGitConfig = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`/api/basecamp/project/${projectId}/git-config`);
      if (!response.ok) {
        if (response.status === 503) {
          toast.warning('Database temporarily unavailable', {
            description: 'Postgres did not respond in time. Check Docker / npm start, then retry.',
          });
        }
        throw new Error('Failed to fetch git config');
      }
      const data = await response.json();
      if (data.success) {
        setGitConfig(data.gitConfig);
        setLocalGitPath(data.gitConfig.local_git_path || '');
        setRemoteGitUrl(data.gitConfig.remote_git_url || '');
        setDefaultBranch(data.gitConfig.default_branch || 'main');
      }
    } catch (error) {
      console.error('[GitPathConfig] Error fetching config:', error);
      // Don't toast: "no git path" is expected; badge already shows "No git path configured"
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetchWithAuth(`/api/basecamp/project/${projectId}/git-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          local_git_path: localGitPath || null,
          remote_git_url: remoteGitUrl || null,
          default_branch: defaultBranch || 'main'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || errorData.message || 'Failed to save');
      }

      const data = await response.json();
      if (data.success) {
        setGitConfig(data.gitConfig);
        toast.success('Git configuration saved');
        setIsOpen(false);
        onUpdate?.();
        window.dispatchEvent(
          new CustomEvent('flownotes-basecamp-git-config-saved', {
            detail: { projectId: projectId != null ? String(projectId) : undefined },
          })
        );
      }
    } catch (error) {
      console.error('[GitPathConfig] Error saving config:', error);
      toast.error('Failed to save git configuration', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasGitPath = gitConfig?.local_git_path;

  if (variant === 'badge') {
    return (
      <>
        {hasGitPath ? (
          <Badge
            variant="outline"
            className="border-green-500/20 bg-green-500/10 text-green-600 cursor-pointer hover:bg-green-500/20"
            onClick={() => setIsOpen(true)}
            title="Click to configure git path"
          >
            <Code2 className="h-3 w-3 mr-1" />
            Git: {gitConfig.local_git_path}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-yellow-500/20 bg-yellow-500/10 text-yellow-600 cursor-pointer hover:bg-yellow-500/20"
            onClick={() => setIsOpen(true)}
            title="Click to configure git path"
          >
            <AlertCircle className="h-3 w-3 mr-1" />
            No git path
          </Badge>
        )}

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Configure Git Path</DialogTitle>
              <DialogDescription>
                Set git repository path for {projectName}
              </DialogDescription>
            </DialogHeader>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="local-git-path">Local Git Path</Label>
                  <Input
                    id="local-git-path"
                    placeholder="e.g., whitebark-pine-ecosystem"
                    value={localGitPath}
                    onChange={(e) => setLocalGitPath(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Relative path from /Users/rowan/hopwork/clients/
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="remote-git-url">Remote Git URL (optional)</Label>
                  <Input
                    id="remote-git-url"
                    placeholder="e.g., git@github.com:hopstudios/repo.git"
                    value={remoteGitUrl}
                    onChange={(e) => setRemoteGitUrl(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="default-branch">Default Branch</Label>
                  <Input
                    id="default-branch"
                    placeholder="main"
                    value={defaultBranch}
                    onChange={(e) => setDefaultBranch(e.target.value)}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving || isLoading}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (variant === 'button') {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="gap-2"
        >
          {hasGitPath ? (
            <>
              <Code2 className="h-4 w-4" />
              Git: {gitConfig.local_git_path}
            </>
          ) : (
            <>
              <Settings className="h-4 w-4" />
              Configure Git Path
            </>
          )}
        </Button>

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Configure Git Path</DialogTitle>
              <DialogDescription>
                Set git repository path for {projectName}
              </DialogDescription>
            </DialogHeader>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="local-git-path">Local Git Path</Label>
                  <Input
                    id="local-git-path"
                    placeholder="e.g., whitebark-pine-ecosystem"
                    value={localGitPath}
                    onChange={(e) => setLocalGitPath(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Relative path from /Users/rowan/hopwork/clients/
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="remote-git-url">Remote Git URL (optional)</Label>
                  <Input
                    id="remote-git-url"
                    placeholder="e.g., git@github.com:hopstudios/repo.git"
                    value={remoteGitUrl}
                    onChange={(e) => setRemoteGitUrl(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="default-branch">Default Branch</Label>
                  <Input
                    id="default-branch"
                    placeholder="main"
                    value={defaultBranch}
                    onChange={(e) => setDefaultBranch(e.target.value)}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving || isLoading}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Inline variant
  return (
    <div className="flex items-center gap-2">
      {hasGitPath ? (
        <span className="text-sm text-muted-foreground">
          <Code2 className="h-3 w-3 inline mr-1" />
          Git: {gitConfig.local_git_path}
        </span>
      ) : (
        <span className="text-sm text-yellow-600">
          <AlertCircle className="h-3 w-3 inline mr-1" />
          No git path configured
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="h-6 px-2 text-xs"
      >
        <Settings className="h-3 w-3" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configure Git Path</DialogTitle>
            <DialogDescription>
              Set git repository path for {projectName}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="local-git-path">Local Git Path</Label>
                <Input
                  id="local-git-path"
                  placeholder="e.g., whitebark-pine-ecosystem"
                  value={localGitPath}
                  onChange={(e) => setLocalGitPath(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Relative path from /Users/rowan/hopwork/clients/
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="remote-git-url">Remote Git URL (optional)</Label>
                <Input
                  id="remote-git-url"
                  placeholder="e.g., git@github.com:hopstudios/repo.git"
                  value={remoteGitUrl}
                  onChange={(e) => setRemoteGitUrl(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="default-branch">Default Branch</Label>
                <Input
                  id="default-branch"
                  placeholder="main"
                  value={defaultBranch}
                  onChange={(e) => setDefaultBranch(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || isLoading}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
